<template>
  <div class="content sub">
    <div class="inner_box">
      <h2 class="title_sub">
        {{ articleList.length > 0 ? articleList[0].recommendTitle : '' }}
      </h2>

      <template v-if="articleList.length < 1">
        <p class="text_none font_p2 mt24">
          콘텐츠가 없습니다
        </p>
      </template>
      <template v-else>
        <column-list class="mt24">
          <list-item-07
            v-for="item in articleList"
            :id="item.postSeq"
            :key="item.postSeq"
            :title="item.title"
            :content="item.lineSummary ?? ''"
            :thumbnail="thumbnailImage(item.thumbnailImageId ?? '')"
            :tags="convertCommaToList(item.tags, 4)"
            :writer-id="item.businessSeq"
            :writer-en-id="item.businessId"
            :writer-name="item.companyName"
            :writer-thumbnail="thumbnailImageWithOptions(item.logoImageId ?? '', {w: 38})"
            :create-date="convertDate(item.creDt)"
            :read-yn="item.readYn"
            @click-item="() => handleNavigateToPost(item)"
            @click-writer="(data: any) => navigateToBusiness(data.writerEnId)"
            @click-tag="(tag: string) => navigateToSearchContents(tag, SEARCH_AFFECT_TYPE.Related)"
          >
            <template #icon>
              <button-bookmark
                :id="item.postSeq"
                :bookmark-yn="item.likeYn"
                @update="(bookmark) => handleUpdateBookmark(bookmark, item)"
              />
            </template>
          </list-item-07>
        </column-list>
      </template>

      <list-infinity-scroll ref="infinityScrollRef" :more="handleListMore" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { IPostSummary } from '~/modules/content/types/model'
import useContentNavigator from '~/modules/content/composables/navigate'
import { useImagesThumbnail } from '~/composables/images/thumbnail'
import PostService from '~/modules/content/services/post-service'

import { convertDate, convertCommaToList } from '~/utils/convert'
import { SEARCH_AFFECT_TYPE, AFFECT_TYPE } from '~/modules/content/types/type'
import useContent from '~/modules/content/composables/content'
import useContentTracking from '~/modules/content/composables/tracking'

definePageMeta({
  key: route => route.fullPath,
  keepalive: true
})

const route = useRoute()
const { setLayout, removeLayout } = useContent()
const {
  navigateToSearchContents,
  navigateToPostWithAnalysis,
  navigateToBusiness
} = useContentNavigator()
const { trackingByViewContentList, trackingBySelectContent, trackingByBookmarkContentList } = useContentTracking()
const { thumbnailImage, thumbnailImageWithOptions } = useImagesThumbnail()

const postService = new PostService()

const page = reactive({ current: 1, cntPerPage: 10, totalCount: 0 })
const articleList = ref<IPostSummary[]>([])
const infinityScrollRef = ref<any>(null)

useHead({
  title: '오운드의 아티클 오마카세 - 오운드 OUND',
  meta: [
    { hid: 'og:title', property: 'og:title', content: '오운드의 아티클 오마카세 - 오운드 OUND' },
    { hid: 'description', property: 'description', content: '오운드가 엄선한 기업 아티클들을 모았습니다. 클릭 한 번으로 최신 기업 이슈들을 알아보세요' },
    { hid: 'og:description', property: 'og:description', content: '오운드가 엄선한 기업 아티클들을 모았습니다. 클릭 한 번으로 최신 기업 이슈들을 알아보세요' }
  ]
})

onBeforeMount(async () => {
  await init()
})

onMounted(() => {
  setLayout('sub')

  trackingByViewContentList()
})
onUnmounted(() => removeLayout('sub'))
onActivated(() => setLayout('sub'))
onDeactivated(() => removeLayout('sub'))

const init = async () => {
  await fetchList(1)
}

const fetchList = async (currentPage: number) => {
  page.current = currentPage

  const recommendSeq = Number(route.params.id ?? 0)
  const response = await postService.getPostListByRecommend({
    currentPage: page.current, cntPerPage: page.cntPerPage, recommendSeq
  })

  if (page.current === 1) {
    articleList.value = response.list
    infinityScrollRef.value && infinityScrollRef.value.init()
  } else {
    articleList.value.push(...response.list)
  }

  page.totalCount = response.totalCnt
  if (articleList.value.length >= page.totalCount) {
    infinityScrollRef.value && infinityScrollRef.value.setEnded(true)
  }
}

const handleListMore = async () => {
  await fetchList(page.current + 1)
}

const handleNavigateToPost = (data: any) => {
  trackingBySelectContent({
    postSeq: data.postSeq,
    title: data.title,
    categoryNo: data.categoryNoDepth1,
    categoryNo2: data.categoryNoDepth2,
    companyName: data.companyName
  })
  navigateToPostWithAnalysis(data.postSeq, { query: { writer: data.companyName, category1: data.categoryNoDepth1, category2: data.categoryNoDepth2, affect_type: AFFECT_TYPE.RECOMMEND } })
}

const handleUpdateBookmark = (bookmarkData: any, data: any) => {
  if (bookmarkData.bookmarkYn === 'Y') {
    trackingByBookmarkContentList({
      postSeq: data.postSeq,
      title: data.title,
      categoryNo: data.categoryNoDepth1,
      categoryNo2: data.categoryNoDepth2,
      companyName: data.companyName
    })
  }
}

</script>

<style scoped>
.content.sub{ position:relative;padding:40px 0 100px; }

@media screen and (max-width: 768px) {
  .content.sub{ padding:12px 0 80px; }
}
</style>
